import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'

// action types
export const GET_MACROSES = 'getMacroses'
export const GET_MACROS = 'getMacros'
export const ADD_MACROS = 'addMacros'
export const UPDATE_MACROS = 'updateMacros'
export const DELETE_MACROS = 'deleteMacros'

// mutation types
export const SAVE_MACROSES = 'saveMacroses'
export const CLEAR_MACROSES = 'clearMacroses'
export const SAVE_MACROS = 'saveMacros'
export const ADD_NEW_MACROS = 'addNewMacros'
export const SELECT_MACROS = 'selectMacros'
export const UNSELECT_MACROS = 'unselectMacros'
export const REMOVE_MACROS = 'removeMacros'
export const SWITCH_MACROS_EDITABLE_STATE = 'switchMacrosEditableState'
export const ADD_NEW_MACROS_FIELD = 'addNewMacrosField'
export const REMOVE_NEW_MACROS_FIELD = 'removeNewMacrosField'
export const SAVE_MANAGE_MACROS_ERRORS = 'saveManageMacrosErrors'
export const CLEAR_MANAGE_MACROS_ERRORS = 'clearManageMacrosErrors'
const SET_RESPONSE_STATE = 'setResponseState'
export const TOAST_MACROSES = 'showToast/SettingsMacroses'

const DEFAULT_SELECTED_MACROS = {
  _id: null,
  name: '',
  value: '',
  server_id: null,
  editable: false,
}

// returns macros based on _id
function findMacros(state, id) {
  return state.macrosesList.filter(item => item._id === id)[0]
}

// returns macros error field based on _id
function findMacrosError(state, id) {
  return state.manageMacrosErrors.filter(item => item._id === id)[0]
}

// back-end doesn't require all object keys that I use on front-end
function convertoForBackend(macros, method) {
  const income = { ... macros }
  const requiredKeys = {
    add: ['name', 'value', 'server_id'],
    update: ['value', 'server_id']
  }

  Object.keys(income).forEach(key => {
    if (!requiredKeys[method].includes(key) || !income[key]) {
      delete income[key]
    }
  })

  return income
}

const state = {
  macrosesList: [],

  selectedMacros: { ...DEFAULT_SELECTED_MACROS },
  selectedMacrosId: null,
  manageMacrosErrors: [],

  responses: {
    getMacroses: false,
    getMacros: false,
    addMacros: false,
    updateMacros: false,
    deleteMacros: false,
  },
}

const getters = {
  macrosesList(state) {
    return state.macrosesList
  },
  totalMacroses(state) {
    return state.macrosesList.filter(i => i._id).length
  },
  macrosesEmpty(state) {
    return state.macrosesList.length == 0
  },
  selectedMacros(state) {
    return state.selectedMacros
  },
  selectedMacrosId(state) {
    return state.selectedMacrosId
  },
  noEmptyMacrosFields(state) {
    return state.macrosesList.filter(i => !i._id).length === 0
  },
  isNewMacrosField: state => id => {
    return state.macrosesList.filter(i => !i._id).length > 0 && id === null
  },
  manageMacrosErrors(state) {
    return state.manageMacrosErrors
  },
  macrosFieldInvalidState: state => (id, field) => {
    const target = findMacrosError(state, id)
    if (target) {
      return target.errors.some(err => err.field == field) ? false : null
    }
  },
  macrosesResponses: state => type => {
    return state.responses[type]
  },
}

const actions = {
  [GET_MACROSES](context, { server_id = undefined }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getMacroses', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(process.env.VUE_APP_MACROSES, { server_id })
          .then(({ data }) => {
            context.commit(SAVE_MACROSES, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_MACROSES, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getMacroses', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_MACROS](context, id) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'getMacros', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_MACROS}/${id}`)
          .then(({ data }) => {
            context.commit(SAVE_MACROS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_MACROSES, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'getMacros', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [ADD_MACROS](context, macros) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'addMacros', status: true })
        eventBus.$emit('startProgress')
        const payload = convertoForBackend(macros, 'add')
        ApiService.post(process.env.VUE_APP_MACROS, payload)
          .then(({ data }) => {
            eventBus.$emit(TOAST_MACROSES, {
              message: data.message,
              variant: 'success'
            })
            context.commit(SWITCH_MACROS_EDITABLE_STATE, {
              macros: macros,
              status: false,
            })
            context.commit(REMOVE_NEW_MACROS_FIELD)
            context.commit(ADD_NEW_MACROS, data)
            eventBus.$emit(TOAST_MACROSES, {
              message: 'New macros added',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_MACROS_ERRORS, {
              _id: macros._id,
              errors: error.errors
            })
            eventBus.$emit(TOAST_MACROSES, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'addMacros', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [UPDATE_MACROS](context, macros) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'updateMacros', status: true })
        eventBus.$emit('startProgress')
        const payload = convertoForBackend(macros, 'update')
        ApiService.put(`${process.env.VUE_APP_MACROS}/${macros._id}`, payload)
          .then(({ data }) => {
            eventBus.$emit(TOAST_MACROSES, {
              message: data.message,
              variant: 'success'
            })
            context.commit(SWITCH_MACROS_EDITABLE_STATE, {
              macros: macros,
              status: false,
            })
            context.dispatch(GET_MACROS, macros._id)
            resolve(macros)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_MACROS_ERRORS, {
              _id: macros._id,
              errors: error.errors
            })
            eventBus.$emit(TOAST_MACROSES, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'updateMacros', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_MACROS](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteMacros', status: true })
        eventBus.$emit('startProgress')
        ApiService.delete(`${process.env.VUE_APP_MACROS}/${context.state.selectedMacrosId}`)
          .then(({ data }) => {
            eventBus.$emit(TOAST_MACROSES, {
              message: data.message,
              variant: 'success'
            })
            context.commit(REMOVE_MACROS, state.selectedMacrosId)
            resolve('success')
          })
          .catch(err  => {
            const error = err.response.data
            eventBus.$emit(TOAST_MACROSES, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteMacros', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_MACROSES](state, macroses) {
    if (macroses.length > 0) {
      macroses.forEach(macro => {
        macro.editable = false
      })
      state.macrosesList = macroses
    }
  },
  [SAVE_MACROS](state, macros) {
    const target = findMacros(state, macros._id)
    if (target) {
      Object.keys(macros).forEach(key => {
        Vue.set(target, key, macros[key])
      })
    }
  },
  [CLEAR_MACROSES](state) {
    state.macrosesList = []
  },
  [ADD_NEW_MACROS](state, macros) {
    state.macrosesList.push(macros)
  },
  [SELECT_MACROS](state, macros) {
    state.selectedMacros = macros
    state.selectedMacrosId = macros._id
  },
  [UNSELECT_MACROS](state) {
    state.selectedMacros = { ...DEFAULT_SELECTED_MACROS }
    state.selectedMacrosId = null
  },
  [REMOVE_MACROS](state, id) {
    const exclude = state.macrosesList.filter(i => i._id !== id)
    state.macrosesList = exclude
  },
  [SWITCH_MACROS_EDITABLE_STATE](state, { macros, status }) {
    const target = findMacros(state, macros._id)

    // this function checks if user decided to switch editable mode
    // if he did, function creates a snapshot of how macros was before switching
    // otherwise function restores target values from that snapshot and deletes it
    function handleSnapshot(status, macros) {
      if (status) {
        target.snapshot = { name: macros.name, value: macros.value }
      } else {
        if (target.snapshot) {
          Object.keys(target.snapshot).forEach(key => {
            Vue.set(target, key, target.snapshot[key])
          })
          delete target.snapshot
        }
      }
    }

    if (target) {
      Vue.set(target, 'editable', status)
      handleSnapshot(status, macros)
    }
  },
  [ADD_NEW_MACROS_FIELD](state, { server_id = undefined }) {
    const canAdd = state.macrosesList.filter(i => !i._id).length === 0

    if (canAdd) {
      const newMacrosTemplate = { ...DEFAULT_SELECTED_MACROS }
      newMacrosTemplate.editable = true
      if (server_id) {
        newMacrosTemplate.server_id = server_id
      }
      state.macrosesList.push(newMacrosTemplate)
    }
  },
  [REMOVE_NEW_MACROS_FIELD](state) {
    let actualMacroses = state.macrosesList.filter(i => i._id)
    state.macrosesList = actualMacroses
  },
  [SAVE_MANAGE_MACROS_ERRORS](state, error) {
    state.manageMacrosErrors.push(error)
  },
  [CLEAR_MANAGE_MACROS_ERRORS](state, id) {
    const target = findMacrosError(state, id)
    if (target) {
      state.manageMacrosErrors = state.manageMacrosErrors.filter(i => i._id !== id)
    }
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
